/**
 * API Client Module
 *
 * Provides a centralized, typed HTTP client for communicating with the Laravel backend.
 * All service modules (authService, workOrderService, etc.) use this client
 * rather than calling fetch() directly.
 *
 * Key features:
 * - Automatic Bearer token injection from localStorage for authenticated requests
 * - Consistent JSON request/response handling
 * - Typed error handling via the ApiError class
 * - Query parameter serialization for GET requests
 *
 * Base URL is configured via the REACT_APP_API_URL environment variable,
 * falling back to localhost:8000/api/v1 for local development. The URL
 * must include the /api/v1 version prefix.
 */

/** Base URL for all API requests; set via env var or defaults to local Laravel dev server */
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

/* ============================================
 * KEY CASE CONVERSION UTILITIES
 * ============================================
 * The Laravel backend uses snake_case for JSON keys (matching database columns),
 * while the React frontend follows JavaScript convention with camelCase.
 * These helpers automatically convert keys in both directions so each layer
 * uses its native naming convention without manual mapping.
 */

/** Convert a single snake_case string to camelCase (e.g. "project_name" → "projectName") */
function toCamelCase(str: string): string {
	return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/** Convert a single camelCase string to snake_case (e.g. "projectName" → "project_name") */
function toSnakeCase(str: string): string {
	return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/** Recursively convert all object keys from snake_case to camelCase */
function camelizeKeys(obj: unknown): unknown {
	if (Array.isArray(obj)) {
		return obj.map(camelizeKeys);
	}
	if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
		return Object.fromEntries(
			Object.entries(obj as Record<string, unknown>).map(([key, value]) => [
				toCamelCase(key),
				camelizeKeys(value),
			])
		);
	}
	return obj;
}

/** Recursively convert all object keys from camelCase to snake_case */
function snakifyKeys(obj: unknown): unknown {
	if (Array.isArray(obj)) {
		return obj.map(snakifyKeys);
	}
	if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
		return Object.fromEntries(
			Object.entries(obj as Record<string, unknown>).map(([key, value]) => [
				toSnakeCase(key),
				snakifyKeys(value),
			])
		);
	}
	return obj;
}

/**
 * Configuration options for an API request.
 *
 * @property method - HTTP method (GET, POST, PUT, PATCH, DELETE); defaults to GET
 * @property headers - Additional headers to merge with defaults (e.g., custom Content-Type)
 * @property body - Request body; will be JSON-stringified automatically
 * @property params - Query string parameters; appended to the URL as ?key=value pairs
 */
interface ApiRequestOptions {
	method?: string;
	headers?: Record<string, string>;
	body?: unknown;
	params?: Record<string, string>;
}

/**
 * Standardized wrapper for all API responses.
 *
 * @property data - The parsed response body, typed to the expected shape
 * @property status - HTTP status code (e.g., 200, 201, 404)
 * @property ok - True if the response status was in the 2xx range
 */
interface ApiResponse<T> {
	data: T;
	status: number;
	ok: boolean;
}

/**
 * Custom error class for non-2xx API responses.
 *
 * Thrown by the request() function when the server returns an error status.
 * Consumers can catch ApiError specifically to handle API failures differently
 * from network errors or other exceptions.
 *
 * @property status - The HTTP status code from the failed response
 * @property data - The parsed response body, which often contains validation errors
 *					or error messages from Laravel
 */
class ApiError extends Error {
	status: number;
	data: unknown;

	constructor(message: string, status: number, data?: unknown) {
		super(message);
		this.name = 'ApiError';
		this.status = status;
		this.data = data;
	}
}

/**
 * Retrieves the Sanctum Bearer token from localStorage.
 *
 * The token is stored at login time (see authService.login) and cleared at logout.
 * Returns null if the user is not authenticated, which means the request
 * will be sent without an Authorization header (appropriate for public endpoints).
 *
 * @returns The stored auth token string, or null if not logged in
 */
function getAuthToken(): string | null {
	return localStorage.getItem('auth_token');
}

/**
 * Core request function that all public API methods delegate to.
 *
 * Handles the full request lifecycle:
 * 1. Builds the full URL with optional query parameters
 * 2. Injects the Authorization header if a token exists in localStorage
 * 3. Sends the request with JSON content type
 * 4. Parses the response (JSON or plain text based on Content-Type header)
 * 5. Throws an ApiError for non-2xx responses so callers can handle failures
 *
 * @param endpoint - The API path relative to BASE_URL (e.g., "/work-orders")
 * @param options - Request configuration (method, headers, body, params)
 * @returns A typed ApiResponse containing the parsed data, status, and ok flag
 * @throws ApiError if the response status is not in the 2xx range
 */
async function request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
	const { method = 'GET', headers = {}, body, params } = options;

	// Build full URL, appending query params if provided
	let url = `${BASE_URL}${endpoint}`;
	if (params) {
		const searchParams = new URLSearchParams(params);
		url += `?${searchParams.toString()}`;
	}

	// Inject the Bearer token for authenticated requests
	const token = getAuthToken();
	const requestHeaders: Record<string, string> = {
		'Content-Type': 'application/json',
		...headers,
	};
	if (token) {
		requestHeaders['Authorization'] = `Bearer ${token}`;
	}

	// Execute the HTTP request — convert outgoing body keys to snake_case
	// so the Laravel backend receives its expected naming convention.
	const response = await fetch(url, {
		method,
		headers: requestHeaders,
		body: body ? JSON.stringify(snakifyKeys(body)) : undefined,
	});

	// Parse response body based on Content-Type header.
	// Non-JSON responses (e.g., plain text errors) are cast to T for flexibility.
	let data: T;
	const contentType = response.headers.get('content-type');
	if (contentType?.includes('application/json')) {
		data = await response.json();
	} else {
		data = (await response.text()) as unknown as T;
	}

	// Throw a structured error for non-2xx responses so callers can
	// distinguish API errors from network failures
	if (!response.ok) {
		throw new ApiError(`API request failed: ${response.statusText}`, response.status, data);
	}

	return { data, status: response.status, ok: response.ok };
}

/**
 * Public API client object exposing typed convenience methods for each HTTP verb.
 *
 * Usage:
 *	 const { data } = await api.get<WorkOrder[]>('/work-orders');
 *	 const { data } = await api.post<WorkOrder>('/work-orders', newOrderData);
 */
export const api = {
	/** Send a GET request. Params are appended as query string parameters. */
	get: <T>(endpoint: string, params?: Record<string, string>) =>
		request<T>(endpoint, { method: 'GET', params }),

	/** Send a POST request with a JSON body. Used for creating resources. */
	post: <T>(endpoint: string, body?: unknown) =>
		request<T>(endpoint, { method: 'POST', body }),

	/** Send a PUT request with a JSON body. Used for full resource replacement. */
	put: <T>(endpoint: string, body?: unknown) =>
		request<T>(endpoint, { method: 'PUT', body }),

	/** Send a PATCH request with a JSON body. Used for partial resource updates. */
	patch: <T>(endpoint: string, body?: unknown) =>
		request<T>(endpoint, { method: 'PATCH', body }),

	/** Send a DELETE request. Used for removing resources. */
	delete: <T>(endpoint: string) =>
		request<T>(endpoint, { method: 'DELETE' }),
};

export { ApiError, camelizeKeys };
export type { ApiResponse };
